import { Modal, type ModalProps } from '@app/components'
import { apiClient, useChangeMfa, useMfa, useVerifySoftwareToken } from '@app/lib'
import { CognitoMfaType } from '@app/types'
import { ActionIcon, Alert, Button, Center, Grid, Loader, Space, Text, TextInput } from '@mantine/core'
import { useForm, yupResolver } from '@mantine/form'
import { useClipboard } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { IconAlertCircle, IconCheck, IconCopy } from '@tabler/icons-react'
import { QRCodeSVG } from 'qrcode.react'
import { type FC, useCallback, useState } from 'react'
import * as yup from 'yup'

const validate = yupResolver(
  yup.object({
    code: yup.string().matches(/[0-9]{6}/, {
      message: 'Must be exactly 6 digits long'
    })
  })
)

export interface AuthenticatorSetupModalProps extends Omit<ModalProps, 'children'> {}

export const AuthenticatorSetupModal: FC<AuthenticatorSetupModalProps> = ({ onClose, ...props }) => {
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [secret, setSecret] = useState<string | null>(null)
  const [showCode, setShowCode] = useState(false)
  const clipboard = useClipboard({ timeout: 2000 })
  const { data: mfa } = useMfa()
  const { isPending: isChangingMfa, mutateAsync: changeMfa } = useChangeMfa()
  const { isPending: isVerifyingSoftwareToken, mutateAsync: verifySoftwareToken } = useVerifySoftwareToken()
  const codeForm = useForm({
    initialValues: {
      code: ''
    },
    validate,
    validateInputOnBlur: true,
    validateInputOnChange: true
  })

  const handleClose = useCallback(() => {
    codeForm.reset()
    setError(null)
    setSecret(null)
    onClose()
  }, [codeForm, onClose])
  const onCodeFocus = useCallback(() => setError(null), [])
  const onShowQRCodeClick = useCallback(async () => {
    setLoading(true)

    const { data } = await apiClient.get<{ data: { secret: string }; success: boolean }>(
      `/api/mfa/software_token_secret`
    )

    setLoading(false)
    setSecret(data?.data.secret)
  }, [])
  const onVerifyClick = useCallback(async () => {
    const { data, error, success, message } = await verifySoftwareToken(codeForm.values.code)

    if (success && data === 'SUCCESS') {
      await changeMfa({
        enabled: [...(mfa?.enabled ?? []), CognitoMfaType.Authenticator],
        preferred: CognitoMfaType.Authenticator
      })

      notifications.show({
        color: 'green',
        icon: <IconCheck />,
        message,
        withBorder: true,
        withCloseButton: false
      })

      handleClose()
    } else if (error?.name === 'EnableSoftwareTokenMFAException') {
      setError(
        'The code you entered is incorrect or has expired. Please try again with a new code from your authenticator app.'
      )
    } else if (message) {
      setError(message)
    }
  }, [changeMfa, codeForm.values.code, handleClose, mfa?.enabled, verifySoftwareToken])

  return (
    <Modal
      keepMounted={false}
      isLoading={isVerifyingSoftwareToken || isChangingMfa}
      onClose={handleClose}
      size='lg'
      title='Authenticator Setup'
      {...props}
    >
      <Grid gutter='sm'>
        {error && (
          <Grid.Col span={12}>
            <Alert
              color='red.6'
              icon={<IconAlertCircle />}
            >
              {error}
            </Alert>
          </Grid.Col>
        )}
        <Grid.Col span={1}>
          <Text size='sm'>1</Text>
        </Grid.Col>
        <Grid.Col span={11}>
          <Text
            size='sm'
            fw='bold'
            mb='xs'
          >
            Install an authenticator app on your mobile device.
          </Text>
          <Text size='sm'>
            We recommend <strong>Google Authenticator</strong>, <strong>Microsoft Authenticator</strong>,{' '}
            <strong>Authy</strong>, or <strong>Duo Mobile</strong>. You can find these in the App Store or Google Play.
          </Text>
        </Grid.Col>
        <Grid.Col span={1}>
          <Text size='sm'>2</Text>
        </Grid.Col>
        <Grid.Col span={7}>
          <Text
            fw='bold'
            size='sm'
            mb='xs'
          >
            Scan the QR code with your authenticator app.
          </Text>
          <Text size='sm'>
            If you cannot scan the QR code, click <strong>“Can’t Scan Code?”</strong> to display the code and a
            copy-to-clipboard icon. You can manually enter this code into your app.
          </Text>
        </Grid.Col>
        <Grid.Col
          p='xs'
          span={4}
        >
          {secret ? (
            <>
              <Center>
                <QRCodeSVG
                  size={128}
                  level='M'
                  value={`otpauth://totp/MetrixEnroll?secret=${secret}&issuer=MetrixEnroll`}
                />
              </Center>
              <Button
                compact
                fullWidth
                onClick={() => setShowCode(true)}
                size='xs'
                variant='white'
              >
                Can’t Scan Code?
              </Button>
            </>
          ) : (
            <>
              <Center
                w={128}
                h={128}
                style={{ border: '1px solid black' }}
              >
                {loading ? (
                  <Loader size='sm' />
                ) : (
                  <Button
                    onClick={onShowQRCodeClick}
                    size='xs'
                    variant='white'
                  >
                    Show QR Code
                  </Button>
                )}
              </Center>
              <Space h={20} />
            </>
          )}
        </Grid.Col>
        {secret && showCode ? (
          <Grid.Col
            offset={1}
            span={11}
          >
            <TextInput
              label='Manually enter this code into your app'
              readOnly
              rightSection={
                <ActionIcon onClick={() => clipboard.copy(secret)}>
                  {clipboard.copied ? <IconCheck size={12} /> : <IconCopy size={12} />}
                </ActionIcon>
              }
              value={secret}
              w='100%'
            />
          </Grid.Col>
        ) : null}
        <Grid.Col span={1}>
          <Text size='sm'>3</Text>
        </Grid.Col>
        <Grid.Col span={11}>
          <Text size='sm'>
            Enter the code generated by your authenticator app into the field below and select ‘Verify Code’ to complete
            the setup.
          </Text>
        </Grid.Col>
        <Grid.Col
          offset={1}
          span={11}
        >
          <TextInput
            placeholder='Code'
            onFocus={onCodeFocus}
            size='sm'
            {...codeForm.getInputProps('code')}
          />
        </Grid.Col>
        <Grid.Col
          offset={1}
          span={11}
        >
          <Button
            disabled={isVerifyingSoftwareToken || isChangingMfa || !codeForm.isValid()}
            fullWidth
            onClick={onVerifyClick}
          >
            Verify Code
          </Button>
        </Grid.Col>
      </Grid>
    </Modal>
  )
}
